import { ExploreUpcomingEvent } from '../services/api/exploreService';
import { isStartingSoon, hasStarted } from '../utils/roomStory';
import './EventRail.css';

/**
 * "Tonight" rail (V2 Amethyst WU-03 R6) — a thin feed over ScheduledEvent.
 * Orange "starting soon" chip is the sanctioned occasional-focus-enhancer use
 * (design-system-v2.md §1/§4 attention budget: one orange element at rest).
 * Joining an event = joining/opening its room at start time; there is no
 * reservation flow, so a not-yet-started event is visible but not tappable.
 */

const CATEGORY_GLYPH: Record<string, string> = {
  games: '🎮',
  lobbies: '🛋️',
  interests: '🎯',
  dating: '💘',
};

function glyphFor(category?: string | null): string {
  return CATEGORY_GLYPH[(category ?? 'lobbies').toLowerCase()] ?? '🌐';
}

function timeUntil(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'starting now';
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

interface EventRailProps {
  events: ExploreUpcomingEvent[];
  /** Only invoked for events that have already started (see doc-comment above). */
  onOpen: (event: ExploreUpcomingEvent) => void;
}

export function EventRail({ events, onOpen }: EventRailProps) {
  if (events.length === 0) return null;

  return (
    <div className="event-rail">
      {events.map((e) => {
        const soon = isStartingSoon(e.startsAtUtc);
        const started = hasStarted(e.startsAtUtc);
        return (
          <button
            key={e.id}
            type="button"
            className={`event-card ${soon ? 'starting-soon' : ''}`}
            onClick={() => started && onOpen(e)}
            disabled={!started}
            title={started ? `Join ${e.title}` : `${e.title} starts ${timeUntil(e.startsAtUtc)}`}
          >
            <div className="event-thumb" data-cat={(e.category ?? 'lobbies').toLowerCase()}>
              {e.thumbnailUrl ? (
                <img src={e.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <span aria-hidden="true">{glyphFor(e.category)}</span>
              )}
              {soon && <span className="event-soon-chip">Starting soon</span>}
            </div>
            <div className="event-body">
              <span className="event-title">{e.title}</span>
              <span className="event-meta">{started ? 'Live now — tap to join' : timeUntil(e.startsAtUtc)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
